import {
    useCallback,
    useState,
    Children,
    useMemo,
    type FC
} from "react";
import {
    type LayoutChangeEvent,
    Platform,
    View
} from "react-native";
import type IPaginationProps from "./type";
import type {
    PaginationContextType,
    PaginationVariant
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import PaginationContent from "./components/content";
import PaginationEllipsis from "./components/ellipsis";
import PaginationItem from "./components/item";
import PaginationLoadMore from "./components/loadMore";
import PaginationNext from "./components/next";
import PaginationPageSize from "./components/pageSize";
import PaginationPrevious from "./components/previous";
import PaginationRange from "./components/range";
import PaginationStatus from "./components/status";
import {
    PAGINATION_DEFAULT_PAGE_SIZE,
    PAGINATION_PAGE_SIZE_OPTIONS,
    PaginationContext
} from "./context";
import {
    getPaginationPageCount,
    getPaginationNextPage
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useBreakpoint
} from "../../helpers/pointer";

const Pagination: FC<IPaginationProps> = ({
    pageSizeOptions = PAGINATION_PAGE_SIZE_OPTIONS,
    variant: variantProp = "numbered",
    stepTitleBehaviour = "auto",
    "aria-label": ariaLabel,
    pageSize: pageSizeProp,
    accessibilityLabel,
    isDisabled = false,
    boundaryCount = 1,
    isLoading = false,
    onPageSizeChange,
    siblingCount = 1,
    isError = false,
    customLocalize,
    page: pageProp,
    size: sizeProp,
    hrefExtractor,
    onPageChange,
    customTheme,
    hasNextPage,
    loadedCount,
    errorTitle,
    onLoadMore,
    totalItems,
    unitTitle,
    children,
    style,
    ...props
}) => {
    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const breakpoint = useBreakpoint();

    const [
        pageState,
        setPageState
    ] = useState(1);

    const [
        pageSizeState,
        setPageSizeState
    ] = useState(PAGINATION_DEFAULT_PAGE_SIZE);

    const [
        containerWidth,
        setContainerWidth
    ] = useState(0);

    const isViewCompact = Platform.OS !== "web" || breakpoint === "compact";

    const isNarrow = containerWidth > 0 && containerWidth < shapeConfigs.compactBreakpoint;

    const isCompact = isViewCompact || isNarrow;

    const variant: PaginationVariant = variantProp === "numbered" && isCompact ? "compact" : variantProp;

    const isTableLayout = Children.count(children) > 0;

    const pageSize = pageSizeProp ?? pageSizeState;

    const pageCount = getPaginationPageCount({
        totalItems,
        pageSize
    });

    const page = Math.max(Math.min(pageProp ?? pageState, pageCount ?? Infinity), 1);

    const {
        container: containerDynamicStyle
    } = useStyles({
        isTableLayout,
        spaces
    });

    const handlePageCommit = useCallback((nextPage: number) => {
        if(pageProp === undefined) {
            setPageState(nextPage);
        }

        onPageChange?.(nextPage);
    }, [
        onPageChange,
        pageProp
    ]);

    const handlePageChange = useCallback((nextPage: number) => {
        handlePageCommit(Math.max(Math.min(nextPage, pageCount ?? Infinity), 1));
    }, [
        handlePageCommit,
        pageCount
    ]);

    const handlePageSizeChange = useCallback((newSize: number) => {
        const nextPage = getPaginationNextPage({
            pageSize,
            newSize,
            page
        });

        if(pageSizeProp === undefined) {
            setPageSizeState(newSize);
        }

        onPageSizeChange?.(newSize);

        if(nextPage !== page) {
            handlePageCommit(nextPage);
        }
    }, [
        handlePageCommit,
        onPageSizeChange,
        pageSizeProp,
        pageSize,
        page
    ]);

    const contextValue = useMemo<PaginationContextType>(() => ({
        size: sizeProp ?? (isViewCompact ? "large" : "medium"),
        onPageSizeChange: handlePageSizeChange,
        onPageChange: handlePageChange,
        stepTitleBehaviour,
        pageSizeOptions,
        customLocalize,
        boundaryCount,
        hrefExtractor,
        siblingCount,
        customTheme,
        hasNextPage,
        loadedCount,
        errorTitle,
        isDisabled,
        onLoadMore,
        totalItems,
        isCompact,
        isLoading,
        pageCount,
        unitTitle,
        pageSize,
        isError,
        variant,
        page
    }), [
        handlePageSizeChange,
        stepTitleBehaviour,
        handlePageChange,
        pageSizeOptions,
        customLocalize,
        isViewCompact,
        hrefExtractor,
        boundaryCount,
        siblingCount,
        customTheme,
        loadedCount,
        hasNextPage,
        totalItems,
        errorTitle,
        isDisabled,
        onLoadMore,
        pageCount,
        unitTitle,
        isLoading,
        isCompact,
        pageSize,
        sizeProp,
        isError,
        variant,
        page
    ]);

    const onLayout = (event: LayoutChangeEvent) => {
        setContainerWidth(Math.floor(event.nativeEvent.layout.width));
    };

    const renderContent = () => {
        if(isTableLayout) {
            return children;
        }

        if(variant === "loadMore") {
            return <PaginationLoadMore />;
        }

        return <PaginationContent />;
    };

    return <PaginationContext.Provider
        value={contextValue}
    >
        <View
            {...props}
            aria-label={variant === "loadMore" ? undefined : (ariaLabel ?? accessibilityLabel ?? localize("pagination-label"))}
            role={variant === "loadMore" ? undefined : "navigation"}
            style={[
                style,
                stylesheet.container,
                variant === "loadMore" && stylesheet.more,
                isTableLayout && stylesheet.table,
                containerDynamicStyle
            ]}
            onLayout={onLayout}
        >
            {renderContent()}
        </View>
    </PaginationContext.Provider>;
};
export default Object.assign(Pagination, {
    Ellipsis: PaginationEllipsis,
    LoadMore: PaginationLoadMore,
    PageSize: PaginationPageSize,
    Previous: PaginationPrevious,
    Content: PaginationContent,
    Status: PaginationStatus,
    Range: PaginationRange,
    Item: PaginationItem,
    Next: PaginationNext
});
